/**
 * useAdStarts — how each Meta ad started (clicks in its first day / two days / week from its
 * own launch), for the Ads report's like-for-like columns.
 *
 * A finished first week never changes, so those answers are kept for the life of the tab;
 * an ad still in its first week is asked for again after five minutes. Asks for nothing when
 * signed out (the dev preview renders the report signed out — see useAdImages).
 */
import { useEffect, useMemo, useState } from 'react';
import { auth, functions } from '../firebase';
import { AnalyticsRepository } from '../models/AnalyticsRepository';

const known = new Map(); // key → { at, stats } (stats null = asked, nothing found)
const TTL = 5 * 60 * 1000;
const fresh = (e) => e && (e.stats && e.stats.complete ? true : Date.now() - e.at < TTL);

/** @param keys campaign keys of the Meta ads on screen */
export function useAdStarts(keys) {
  const repo = useMemo(() => new AnalyticsRepository(functions), []);
  const wanted = useMemo(() => [...new Set(keys || [])].filter((k) => /^\d{10,}$/.test(k)).sort(), [keys]);
  const signature = wanted.join(',');
  const [, bump] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const missing = wanted.filter((k) => !fresh(known.get(k)));
    if (!missing.length) return undefined;
    if (!auth || !auth.currentUser) return undefined;
    let live = true;
    setLoading(true);
    repo.getAdStarts(missing.slice(0, 30))
      .then((starts) => {
        const at = Date.now();
        missing.forEach((k) => known.set(k, { at, stats: starts[k] || null }));
      })
      .catch(() => { /* the columns show "–": the rest of the report does not depend on this */ })
      .finally(() => { if (live) { setLoading(false); bump((n) => n + 1); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, repo]);

  const starts = {};
  wanted.forEach((k) => { const e = known.get(k); if (e && e.stats) starts[k] = e.stats; });
  return { starts, loading };
}

/** For tests only. */
export function resetAdStartsCache() { known.clear(); }

export default useAdStarts;
